import { LocalDate } from '@js-joda/core';
import { useCallback, useState } from 'react';
import { useToggle } from 'react-use';

import { GuardianExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { GuardianPesel, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useTranslate } from '@dreryk/react-common';
import { Loader, LoaderSize } from '@dreryk/react-components';

import { PersonalDataFormController } from '../../../../../common/common.types';
import { FormSectionHeader } from '../../../../../common/FormSectionHeader';
import { createGuardianExternalIdentifier } from '../../../../../common/GuardianModal/GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModal } from '../../../../../common/GuardianModal/GuardianModal';
import { GuardianModalFormBean } from '../../../../../common/GuardianModal/GuardianModal.types';
import { guardiansTranslationKeys } from '../../../../../translations';
import { GuardiansTable } from '../GuardiansTable/GuardiansTable';

import { useGuardiansQuery } from './PatientGuardians.hooks';

type PatientGuardiansSectionProps = Pick<PersonalDataFormController, 'setValue' | 'watch'> & {
   readonly patientId: number;
};

const ADULTHOOD_YEARS_COUNT = 18;
const shouldBeStatutoryRepresentative = (
   pesel: Pesel | null | undefined,
   birthDate: LocalDate | null | undefined
): boolean => {
   const selectedBirthday = pesel ? pesel.getBirthDate() : birthDate;
   return Boolean(selectedBirthday?.isAfter(LocalDate.now().minusYears(ADULTHOOD_YEARS_COUNT)));
};

// eslint-disable-next-line max-lines-per-function
export const PatientGuardians = (props: PatientGuardiansSectionProps): JSX.Element => {
   const { patientId, setValue } = props;
   const { birthDate: patientBirthDate, pesel: patientPesel, guardianPesel: patientGuardianPesel } = props.watch([
      'birthDate',
      'pesel',
      'guardianPesel',
   ]);
   const [guardianModalOpened, toggleGuardianModalOpened] = useToggle(false);
   const [editableGuardianId, setEditableGuardianId] = useState<number | undefined>(undefined);
   const [isIdentifyingGuardianEdited, setIsIdentifyingGuardianEdited] = useState<boolean>(false);
   const { data: guardians, loading, refetch } = useGuardiansQuery(patientId);
   const translate = useTranslate();
   const handleGuardianModalSubmitSuccess = useCallback(
      async (modalFormData: GuardianModalFormBean) => {
         if (isIdentifyingGuardianEdited) {
            const guardianExternalIdentifier = createGuardianExternalIdentifier(modalFormData.guardian!);
            setValue('identifyingGuardian', modalFormData);
            setValue(
               'guardianPesel',
               guardianExternalIdentifier.accept<GuardianPesel | null>({
                  visitPesel: (pesel) => GuardianPesel.of(pesel.asString()),
                  visitPassport: () => null,
                  visitNationalIdentificationNumber: () => null,
               })
            );
         }
         setIsIdentifyingGuardianEdited(false);
         toggleGuardianModalOpened();
         setEditableGuardianId(undefined);
         await refetch();
      },
      [isIdentifyingGuardianEdited, refetch, setValue, toggleGuardianModalOpened]
   );

   const onGuardianModalHide = useCallback(() => {
      toggleGuardianModalOpened();
      setIsIdentifyingGuardianEdited(false);
      setEditableGuardianId(undefined);
   }, [toggleGuardianModalOpened]);

   const handleGuardianEdit = useCallback(
      (guardianId: number, guardianExternalIdentifier: GuardianExternalIdentifier) => {
         const identifingGuardian = patientGuardianPesel
            ? guardianExternalIdentifier.accept<boolean>({
                 visitPesel: (pesel) => patientGuardianPesel.asString() === pesel.asString(),
                 visitPassport: () => false,
                 visitNationalIdentificationNumber: () => false,
              })
            : false;

         if (identifingGuardian) {
            setIsIdentifyingGuardianEdited(true);
         }
         setEditableGuardianId(guardianId);
         toggleGuardianModalOpened();
      },
      [patientGuardianPesel, toggleGuardianModalOpened]
   );

   return (
      <div>
         <FormSectionHeader>{translate(guardiansTranslationKeys.guardians)}</FormSectionHeader>
         {loading ? (
            <Loader size={LoaderSize.MEDIUM} testId={'loader'} />
         ) : (
            <GuardiansTable
               guardians={guardians}
               onEdit={handleGuardianEdit}
               onAddGuardian={toggleGuardianModalOpened}
               onGuardianDeleted={refetch}
            />
         )}
         {guardianModalOpened ? (
            <GuardianModal
               patientId={patientId}
               patientPesel={patientPesel}
               guardianId={editableGuardianId}
               onClose={onGuardianModalHide}
               statutoryRepresentative={
                  editableGuardianId ? undefined : shouldBeStatutoryRepresentative(patientPesel, patientBirthDate)
               }
               isIdentifyingGuardian={isIdentifyingGuardianEdited}
               onSuccess={handleGuardianModalSubmitSuccess}
            />
         ) : null}
      </div>
   );
};
